import { PropsWithChildren, useRef, useState } from 'react';
import { GetServerSideProps } from 'next';
import { LocalBusinessJsonLd, NextSeo, OrganizationJsonLd } from 'next-seo';
import { OpeningHoursSpecification } from 'next-seo/lib/types';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import FooterLogo from '@/assets/img/logo-azul.svg';
import {
  ChipsAccordion,
  ChipsAccordionDetails,
  ChipsAccordionSummary,
} from '@/components/InstitutionalPage/Accordion';
import { Banner } from '@/components/InstitutionalPage/Banner';
import { WEEKDAY_TEST_ID } from '@/components/InstitutionalPage/OpeningHours';
import { getDateFromAvailabilityInterval } from '@/components/InstitutionalPage/OpeningHours/availabilitiesReducer';
import { ShareInstitutionPage } from '@/components/InstitutionalPage/ShareInstitutionPage';
import { AddPortfolio } from '@/components/Modals/Portfolio/AddPortfolio';
import { PortfolioCard } from '@/components/Portfolio/PortfolioCard';
import { ReviewsPortfolio } from '@/components/Portfolio/ReviewsPortfolio';
import { useUser } from '@/contexts/AuthContext';
import { InstitutionalPageProvider } from '@/contexts/InstitutionalPageContext';
import MainContainer from '@/layouts/MainContainer';
import { setupAPIClient } from '@/services/api';
import { themeLight } from '@/themes/light';
import {
  Availability,
  AvailabilityInterval,
  ICompany,
  IPortfolioItem,
  IReviews,
} from '@/types/api/companies';
import { getFirstValueTrue } from '@/utils/arrayUtils';
import {
  AVAILABILITY_STATUS,
  canEditCompanyInstitutionalPage,
  formatCompanyAddress,
  formatHour,
  ReviewsDefault,
  totalAverageReviews,
  WEEKDAYS,
} from '@/utils/components/institutionalPage';
import {
  CURRENT_ENVIRONMENT_URL,
  DEFAULT_TIMEZONE,
  URL_GOOGLE_MAPS,
} from '@/utils/constants';
import { ROUTER_PAGES } from '@/utils/constants';
import { cnpjMask, phoneMask } from '@/utils/masks';
import { Facebook, Instagram, LinkedIn, Star } from '@mui/icons-material';
import AccessAlarmOutlinedIcon from '@mui/icons-material/AccessAlarmOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import ShareIcon from '@mui/icons-material/Share';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Button,
  Stack,
  styled,
  Typography,
  Chip,
  IconButton,
  BoxProps,
  Divider,
} from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { AxiosError } from 'axios';

interface IInstitutionalPageProps {
  company: ICompany;
  portfolio: IPortfolioItem[];
  availabilities: Availability[];
  reviews: IReviews[];
  reviewsWithParams: IReviews[];
  availabilityStatus: keyof typeof AVAILABILITY_STATUS;
}

interface ISectionContainerProps {
  title: string;
  fallback?: string;
}

const CHIPS_HEIGHT = 32;

const RowInfos = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  '& a': {
    color: theme.palette.primary.main,
  },
}));

const SectionContainer = ({
  title,
  fallback,
  children,
  className = 'content',
  ...props
}: PropsWithChildren<ISectionContainerProps> & BoxProps) => {
  return (
    <Box component="section">
      <Typography component="h2" variant="h6" marginBottom={2}>
        {title}
      </Typography>

      <Box className={className} {...props}>
        {!children && fallback ? (
          <Typography
            color="text.secondary"
            component="p"
            fontSize="14px"
            marginY="38px"
            textAlign="center"
          >
            {fallback}
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};

const InstitutionalPage = ({
  company,
  portfolio: initialPortfolioItems,
  availabilities: initialAvailabilities,
  reviews: initialReviews,
  reviewsWithParams,
}: IInstitutionalPageProps) => {
  const router = useRouter();
  const { user, isAuthenticated } = useUser();
  const categoriesRef = useRef<HTMLDivElement | null>(null);
  const shippingLocationsRef = useRef<HTMLDivElement | null>(null);
  const [availabilityStatus] = useState(
    getAvailabilityStatus(initialAvailabilities)
  );
  const [portfolioItems, setPortfolioItems] = useState<IPortfolioItem[]>(
    initialPortfolioItems
  );

  const [reviews, setReviews] = useState<IReviews[]>(initialReviews);

  const [portfolioItemIdToEdit, setPortfolioItemIdToEdit] = useState<
    number | undefined
  >();
  const [isOpenShareInstitutionalPage, setIsOpenInstitutionalPage] =
    useState(false);

  const portfolioItemToEdit = portfolioItems.find(
    value => value.id === portfolioItemIdToEdit
  );

  const userCanEditInstitutionalPage = canEditCompanyInstitutionalPage(
    company,
    user
  );

  const companyTitle = (company.display_name || company.full_name)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  const companyTitleWithObraPlay = `${companyTitle} | Obra Play`;

  const institutionalPageUrl = `${CURRENT_ENVIRONMENT_URL}/site/${company.slug}`;

  function createGoogleMapsUrl(company: ICompany) {
    return `${URL_GOOGLE_MAPS}/search/?api=1&query=${formatCompanyAddress(
      company
    )}`;
  }

  function isToSeeAllButton(accordionEl: HTMLDivElement | null) {
    return accordionEl && !(accordionEl.clientHeight <= CHIPS_HEIGHT + 8);
  }

  function orderByToday(availabilities: Availability[]): Availability[] {
    const today = new Date().getDay();
    const availabilitiesCopy = [...availabilities];

    return availabilitiesCopy.sort((a, b) => {
      if (a.weekday === today) {
        return -1;
      }

      if (b.weekday === today) {
        return 1;
      }

      return a.weekday - b.weekday;
    });
  }

  function sortByCreatedAt(a: AvailabilityInterval, b: AvailabilityInterval) {
    return new Date(a.created_at) < new Date(b.created_at) ? -1 : 1;
  }

  function getAvailabilityStatus(availabilities: Availability[]) {
    const today = new Date();
    const availabilityOfToday = availabilities.find(
      ({ weekday }) => weekday === today.getDay()
    );

    if (availabilityOfToday?.is_always_open) {
      return AVAILABILITY_STATUS.Open;
    }

    if (!availabilityOfToday || availabilityOfToday.intervals.length === 0) {
      return AVAILABILITY_STATUS.Closed;
    }

    const todayFormatted = getDateFromAvailabilityInterval(
      Intl.DateTimeFormat('pt-br', {
        timeStyle: 'short',
        timeZone: availabilityOfToday.intervals[0].timezone || DEFAULT_TIMEZONE,
      }).format(today) + ':00'
    );

    return availabilityOfToday.intervals.reduce((acc, current) => {
      if (
        todayFormatted >= getDateFromAvailabilityInterval(current.start) &&
        todayFormatted < getDateFromAvailabilityInterval(current.end)
      ) {
        return AVAILABILITY_STATUS.Open;
      }

      return acc;
    }, AVAILABILITY_STATUS.Closed);
  }

  function getLocalBusinessOpeningHours({
    intervals,
    is_always_open,
    weekday,
  }: Availability) {
    const openingHoursSpecificationDefault = {
      type: 'OpeningHoursSpecification',
      opens: '00:00',
      closes: '00:00',
      dayOfWeek: WEEKDAY_TEST_ID[weekday],
    };

    if (is_always_open) {
      return {
        ...openingHoursSpecificationDefault,
        opens: '00:00',
        closes: '23:59',
      };
    }

    if (intervals.length > 1) {
      return intervals.map(({ start, end }) => {
        return {
          ...openingHoursSpecificationDefault,
          opens: start,
          closes: end,
        };
      });
    }

    return openingHoursSpecificationDefault;
  }

  return (
    <InstitutionalPageProvider initialCompany={company}>
      <Head>
        <title>{companyTitleWithObraPlay}</title>

        <meta
          name="application-name"
          content={`${companyTitle} | Obra Play`}
          key="application-name"
        />
      </Head>

      <NextSeo
        description={company.description}
        openGraph={{
          type: 'website',
          url: institutionalPageUrl,
          title: companyTitleWithObraPlay,
          siteName: companyTitleWithObraPlay,
          description: company.description || company.slogan,
          images: [
            {
              url: company.background_image,
              alt: `${companyTitle} banner`,
              width: 900,
              height: 200,
            },
            {
              url: company.logo,
              alt: `${companyTitle} logo`,
              height: 256,
              width: 256,
            },
          ],
        }}
      />
      <OrganizationJsonLd
        id={company.slug}
        logo={company.logo}
        legalName={companyTitle}
        name={companyTitleWithObraPlay}
        slogan={company.slogan}
        contactPoint={[
          {
            telephone: company.phone,
            contactType: 'Serviço do consumidor',
            email: company.email,
            areaServed: 'BR',
          },
        ]}
        url={institutionalPageUrl}
      />
      <LocalBusinessJsonLd
        type="Store"
        id={company.slug}
        name={companyTitle}
        description={company.description || company.slogan}
        images={[company.logo, company.background_image]}
        telephone={company.phone}
        email={company.email}
        url={institutionalPageUrl}
        address={{
          streetAddress: company.street,
          addressLocality: company.city,
          addressRegion: company.state,
          postalCode: company.zipcode,
          addressCountry: 'BR',
        }}
        openingHours={initialAvailabilities.reduce(
          (acc, current) => acc.concat(getLocalBusinessOpeningHours(current)),
          [] as OpeningHoursSpecification[]
        )}
      />

      <Box
        sx={{
          backgroundColor: 'background.default',
          height: '100%',
        }}
      >
        <Banner />

        <MainContainer
          component="main"
          sx={{
            paddingBottom: 3,
            [themeLight.breakpoints.up('md')]: {
              paddingX: 0,
            },
          }}
        >
          <Stack
            spacing={3}
            sx={{
              '& .content': {
                backgroundColor: 'background.paper',
                borderRadius: '12px',
                padding: 2,
              },
            }}
          >
            <Stack
              className="content"
              component="section"
              sx={theme => ({
                alignItems: 'center',
                flexDirection: 'column',
                gap: 3,
                justifyContent: 'space-between',
                textAlign: 'center',
                marginX: 'auto',
                width: '100%',
                [theme.breakpoints.up('md')]: {
                  alignItems: 'flex-start',
                  flexDirection: 'row',
                  textAlign: 'left',
                  width: '100%',
                },
              })}
            >
              <Box
                paddingTop={3}
                sx={theme => ({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  [theme.breakpoints.up('md')]: {
                    display: 'block',
                  },
                })}
              >
                <Typography
                  component="h1"
                  fontWeight="bold"
                  textTransform="capitalize"
                  variant="h5"
                >
                  {companyTitle}
                </Typography>

                {company.slogan && (
                  <Typography
                    color="text.secondary"
                    component="p"
                    marginX="auto"
                    variant="body2"
                  >
                    {company.slogan}
                  </Typography>
                )}
                <Box display="flex" alignItems="center" gap={3} marginTop={1}>
                  {company.verified_cnpj && (
                    <Stack
                      alignItems="center"
                      component="span"
                      color="#8BC34A"
                      direction="row"
                      justifyContent="center"
                      spacing={0.5}
                      sx={theme => ({
                        [theme.breakpoints.up('md')]: {
                          justifyContent: 'flex-start',
                        },
                      })}
                    >
                      <VerifiedIcon fontSize="small" />
                      <Typography component="p" variant="body2">
                        Certificado
                      </Typography>
                    </Stack>
                  )}
                  {reviews.length > 0 ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ color: '#FAAF00' }} />
                      <Typography variant="body2" color="#FAAF00">
                        {totalAverageReviews(ReviewsDefault(reviews))
                          .toFixed(1)
                          .replace('.', ',')}{' '}
                        Avaliações
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ color: '#808080' }} />
                      <Typography variant="body2" color="#808080">
                        Sem avaliações
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                justifyContent="center"
              >
                {userCanEditInstitutionalPage && (
                  <Link
                    href={`/site/${router.query.company_slug}/editar`}
                    passHref
                  >
                    <Box component="a">
                      <Button
                        sx={theme => ({
                          whiteSpace: 'nowrap',
                          [theme.breakpoints.up('md')]: {
                            display: 'none',
                          },
                        })}
                        startIcon={<EditOutlinedIcon />}
                        variant="outlined"
                      >
                        Editar página
                      </Button>
                      <IconButton
                        aria-label="Editar página institucional"
                        sx={theme => ({
                          display: 'none',
                          height: theme.spacing(5),
                          [theme.breakpoints.up('md')]: {
                            display: 'block',
                          },
                        })}
                      >
                        <EditOutlinedIcon color="primary" />
                      </IconButton>
                    </Box>
                  </Link>
                )}
                <Button
                  onClick={() => setIsOpenInstitutionalPage(true)}
                  sx={theme => ({
                    whiteSpace: 'nowrap',
                    [theme.breakpoints.up('md')]: {
                      display: 'none',
                    },
                  })}
                  startIcon={<ShareIcon />}
                  variant="contained"
                >
                  Compartilhar
                </Button>

                <IconButton
                  aria-label="Compartilhar página institucional"
                  onClick={() => setIsOpenInstitutionalPage(true)}
                  sx={theme => ({
                    display: 'none',
                    height: theme.spacing(5),
                    [theme.breakpoints.up('md')]: {
                      display: 'block',
                    },
                  })}
                >
                  <ShareIcon color="primary" />
                </IconButton>
              </Stack>
            </Stack>

            <Box className="content" component="section">
              <Box className="skeleton" height="158px">
                <iframe
                  aria-label="Localização da empresa no google maps"
                  height="158"
                  width="100%"
                  style={{
                    border: 0,
                  }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${formatCompanyAddress(
                    company
                  )}&z=16&output=embed`}
                ></iframe>
              </Box>

              <Stack spacing={1} marginTop={2}>
                {(company.state || company.street || company.city) && (
                  <RowInfos as="span">
                    <RoomOutlinedIcon fontSize="small" />
                    <Link href={createGoogleMapsUrl(company)} passHref>
                      <Typography
                        component="a"
                        target="_blank"
                        textTransform="capitalize"
                        variant="body2"
                      >
                        {formatCompanyAddress(company)}
                      </Typography>
                    </Link>
                  </RowInfos>
                )}
                {company.email && (
                  <RowInfos as="span">
                    <EmailOutlinedIcon fontSize="small" />
                    <Link href={`mailto:${company.email}`} passHref>
                      <Typography component="a" variant="body2" target="_blank">
                        {company.email}
                      </Typography>
                    </Link>
                  </RowInfos>
                )}
                {company.phone && (
                  <RowInfos as="span">
                    <LocalPhoneOutlinedIcon fontSize="small" />
                    <Link href={`tel:${company.phone}`} passHref>
                      <Typography component="a" variant="body2" target="_blank">
                        {phoneMask(company.phone)}
                      </Typography>
                    </Link>
                  </RowInfos>
                )}
                <Box>
                  <MuiAccordion elevation={0} disableGutters>
                    <MuiAccordionSummary
                      aria-controls="availabilities-status-content"
                      id="availabilities-status-header"
                      expandIcon={<ExpandMoreIcon sx={{ marginTop: 1 }} />}
                      sx={{
                        padding: 0,
                        minHeight: 'auto',
                        '& .MuiAccordionSummary-content': {
                          margin: 0,
                        },
                      }}
                    >
                      <RowInfos as="span">
                        <AccessAlarmOutlinedIcon fontSize="small" />
                        {AVAILABILITY_STATUS.Open === availabilityStatus ? (
                          <Typography
                            color="#8BC34A"
                            component="p"
                            variant="body2"
                          >
                            Aberto agora
                          </Typography>
                        ) : (
                          <Typography
                            color="error.main"
                            component="p"
                            variant="body2"
                          >
                            Fechado
                          </Typography>
                        )}
                      </RowInfos>
                    </MuiAccordionSummary>
                    <MuiAccordionDetails
                      sx={{
                        padding: '8px 28px',
                      }}
                    >
                      {orderByToday(initialAvailabilities).map(
                        ({ id, intervals, weekday, is_always_open }) => (
                          <RowInfos
                            key={id}
                            width="100%"
                            maxWidth="300px"
                            justifyContent="space-between"
                          >
                            <Typography
                              color="text.secondary"
                              component="p"
                              variant="body2"
                            >
                              {WEEKDAYS[weekday]}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              component="p"
                              variant="body2"
                            >
                              {getFirstValueTrue([
                                is_always_open && ' Aberto 24 horas',
                                intervals.length === 0 && 'Fechado',
                                intervals
                                  .sort(sortByCreatedAt)
                                  .map(
                                    ({ start, end }, i) =>
                                      `${formatHour(start)} às ${formatHour(
                                        end
                                      )} ${
                                        i < intervals.length - 1 ? '- ' : ''
                                      }`
                                  ),
                              ])}
                            </Typography>
                          </RowInfos>
                        )
                      )}
                    </MuiAccordionDetails>
                  </MuiAccordion>
                </Box>
                {company.cnpj && (
                  <RowInfos as="span">
                    <InfoOutlinedIcon fontSize="small" />
                    <Typography component="p" variant="body2">
                      CNPJ {cnpjMask(company.cnpj)}
                    </Typography>
                  </RowInfos>
                )}
                {company.website_url && (
                  <RowInfos as="span">
                    <LanguageOutlinedIcon fontSize="small" />
                    <Link href={company.website_url} passHref target="_blank">
                      <Typography component="a" target="_blank" variant="body2">
                        {company.website_url}
                      </Typography>
                    </Link>
                  </RowInfos>
                )}
              </Stack>

              <Stack
                aria-label="Rodapé"
                component="nav"
                direction="row"
                marginTop={2}
                spacing={2}
              >
                <Button
                  aria-label="Visitar Facebook"
                  disabled={!company.facebook_url}
                  href={company.facebook_url}
                  sx={{
                    color: 'primary',
                    flex: 1,
                    paddingY: '11px',
                  }}
                  target="_blank"
                  variant="outlined"
                >
                  <Facebook fontSize="small" />
                </Button>

                <Button
                  aria-label="Visitar Instagram"
                  disabled={!company.instagram_url}
                  href={company.instagram_url}
                  sx={{
                    color: 'primary',
                    flex: 1,
                    paddingY: '11px',
                  }}
                  target="_blank"
                  variant="outlined"
                >
                  <Instagram fontSize="small" />
                </Button>

                <Button
                  aria-label="Visitar Linkedin"
                  disabled={!company.linkedin_url}
                  href={company.linkedin_url}
                  sx={{
                    color: 'primary',
                    flex: 1,
                    paddingY: '11px',
                  }}
                  target="_blank"
                  variant="outlined"
                >
                  <LinkedIn fontSize="small" />
                </Button>
              </Stack>
            </Box>

            <SectionContainer
              title="Sobre"
              fallback="Ainda não adicionou um sobre"
            >
              {company.description && (
                <Typography
                  color="text.secondary"
                  component="p"
                  variant="body2"
                >
                  {company.description}
                </Typography>
              )}
            </SectionContainer>

            <SectionContainer
              title="Categorias"
              fallback="Ainda não adicionou categorias"
            >
              {company.categories.length > 0 && (
                <ChipsAccordion>
                  <ChipsAccordionSummary
                    aria-controls="availabilities-content"
                    id="availabilities-header"
                    aria-label="Categorias"
                    disabled={!isToSeeAllButton(categoriesRef.current)}
                  />
                  <ChipsAccordionDetails ref={categoriesRef}>
                    {company.categories.map(category => (
                      <Chip
                        key={category.id}
                        label={category.name}
                        sx={{
                          backgroundColor: '#E9E9E9',
                          color: 'rgba(0, 0, 0, 0.3)',
                          '&&:hover': {
                            backgroundColor: '#E9E9E9',
                            color: 'rgba(0, 0, 0, 0.3)',
                          },
                        }}
                      />
                    ))}
                  </ChipsAccordionDetails>
                </ChipsAccordion>
              )}
            </SectionContainer>

            <SectionContainer
              title="Locais de entrega"
              fallback="Ainda não adicionou locais de entrega"
            >
              {company.shipping_locations.length > 0 && (
                <ChipsAccordion>
                  <ChipsAccordionSummary
                    aria-controls="shipping-locations-content"
                    id="shipping-locations-header"
                    aria-label="Locais de entrega"
                    disabled={!isToSeeAllButton(shippingLocationsRef.current)}
                  />
                  <ChipsAccordionDetails ref={shippingLocationsRef}>
                    {company.shipping_locations.map(shippingLocation => (
                      <Chip
                        key={shippingLocation.id}
                        label={shippingLocation[shippingLocation.type].name}
                        sx={{
                          backgroundColor: '#E9E9E9',
                          color: 'rgba(0, 0, 0, 0.3)',
                          '&&:hover': {
                            backgroundColor: '#E9E9E9',
                            color: 'rgba(0, 0, 0, 0.3)',
                          },
                        }}
                      />
                    ))}
                  </ChipsAccordionDetails>
                </ChipsAccordion>
              )}
            </SectionContainer>

            <SectionContainer
              title="Portfólio"
              fallback="Ainda não adicionou um portfolio"
              className={portfolioItems.length === 0 ? undefined : ''}
            >
              {portfolioItems.length > 0 ? (
                <>
                  <PortfolioCard
                    valuesPortfolioCard={portfolioItems.slice(0, 2)}
                    setIsOpenEditPortfolio={setPortfolioItemIdToEdit}
                    company={company}
                  />

                  <Link
                    href={`/site/${router.query.company_slug}/portfolio`}
                    target="_blank"
                  >
                    <Button
                      sx={{
                        display: 'flex',
                        fontSize: '14px',
                        marginLeft: 'auto',
                        marginTop: 1,
                      }}
                    >
                      Ver portfólio completo
                    </Button>
                  </Link>
                </>
              ) : (
                portfolioItems.length === 0 &&
                userCanEditInstitutionalPage && (
                  <Link
                    passHref
                    href={`/site/${company.slug}/portfolio?add-portfolio=true`}
                  >
                    <Typography
                      component="a"
                      color="primary"
                      display="block"
                      textAlign="center"
                      variant="body2"
                    >
                      + Adicionar portfólio
                    </Typography>
                  </Link>
                )
              )}
            </SectionContainer>
            <SectionContainer
              title="Avaliações"
              fallback="Ainda não teve avaliações"
              className={reviews.length === 0 ? undefined : ''}
            >
              {reviews.length > 0 && (
                <>
                  <ReviewsPortfolio
                    reviewsWithParams={reviewsWithParams}
                    reviews={reviews}
                    setReviews={value => setReviews(value)}
                  />
                  <Box sx={{ float: 'right' }}>
                    <Link
                      href={`/site/${router.query.company_slug}/avaliacoes`}
                      passHref
                    >
                      <Button
                        sx={{
                          display: 'flex',
                          fontSize: '14px',
                          marginLeft: 'auto',
                          marginTop: 1,
                        }}
                      >
                        Ver todas as avaliações
                      </Button>
                    </Link>
                  </Box>
                </>
              )}
            </SectionContainer>
          </Stack>
        </MainContainer>
        {!isAuthenticated && (
          <Box
            component="footer"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'white',
              py: 5,
              mt: 3,
            }}
          >
            <Box
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: theme.breakpoints.values['md'],
                px: 2,
              })}
            >
              <Divider sx={{ width: '100%' }}>
                <Link href="https://www.facebook.com/obraplay" passHref>
                  <a target="_blank" rel="noreferrer">
                    <IconButton aria-label="Visitar Facebook do Obra Play">
                      <Facebook color="primary" />
                    </IconButton>
                  </a>
                </Link>
                <Link href="https://www.instagram.com/obra.play/" passHref>
                  <a target="_blank" rel="noreferrer">
                    <IconButton aria-label="Visitar Instagram do Obra Play">
                      <Instagram color="primary" />
                    </IconButton>
                  </a>
                </Link>
                <Link
                  href="https://www.linkedin.com/company/obra-play/"
                  passHref
                >
                  <a target="_blank" rel="noreferrer">
                    <IconButton aria-label="Visitar Linkedin do Obra Play">
                      <LinkedIn color="primary" />
                    </IconButton>
                  </a>
                </Link>
              </Divider>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Image height="26" src={FooterLogo} alt="Logo Obra Play" />
                <Typography color="#808080" variant="body2">
                  Criado por Obra Play ® {new Date().getFullYear()}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  height: '22px',
                  gap: 3,
                }}
              >
                <Link href={ROUTER_PAGES.obra_play} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography color="primary">Visitar site</Typography>
                  </a>
                </Link>
                <Divider orientation="vertical" />
                <Link href={ROUTER_PAGES.cadastro} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography color="primary">Cadastre-se</Typography>
                  </a>
                </Link>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <AddPortfolio
        isOpen={!!portfolioItemIdToEdit}
        setIsOpen={isOpen => {
          if (!isOpen) setPortfolioItemIdToEdit(undefined);
        }}
        isEditPortfolioItem={portfolioItemToEdit}
        setValuesPortfolioItem={portfolioItemUpdated => {
          if (!portfolioItemIdToEdit) return;

          if (!portfolioItemUpdated) {
            setPortfolioItems(prevState =>
              prevState.filter(obj => obj.id !== portfolioItemIdToEdit)
            );
            return;
          }

          setPortfolioItems(prevState =>
            prevState.map(obj =>
              obj.id === portfolioItemIdToEdit ? portfolioItemUpdated : obj
            )
          );
        }}
        companyId={company.id}
      />

      <ShareInstitutionPage
        isOpen={isOpenShareInstitutionalPage}
        onClose={() => setIsOpenInstitutionalPage(false)}
      />
    </InstitutionalPageProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const redirectToHome = {
    redirect: {
      destination: '/home',
      permanent: false,
    },
  };

  try {
    const api = setupAPIClient(context);
    const { company_slug }: { company_slug?: string } = context.query;

    if (company_slug && company_slug.match(/[A-Z]/)) {
      return {
        redirect: {
          destination: `/site/${company_slug.toLowerCase()}`,
          permanent: false,
        },
      };
    }

    const { data: company } = await setupAPIClient(context).get<ICompany>(
      `api/companies/${company_slug}/`
    );

    if (!company.has_active_institutional_page) {
      return redirectToHome;
    }

    const [
      { data: availabilities },
      { data: portfolio },
      {
        data: { results: reviewsWithParams },
      },
      {
        data: { results: reviews },
      },
    ] = await Promise.all([
      api.get<Availability[]>(`api/companies/${company.id}/availabilities/`),
      api.get<IPortfolioItem[]>(`api/companies/${company.id}/portfolio_items/`),
      api.get<{
        results: IReviews[];
      }>(`/api/companies/${company.id}/reviews/`, {
        params: {
          page_size: 3,
        },
      }),
      setupAPIClient(context).get<{
        results: IReviews[];
      }>(`/api/companies/${company.id}/reviews/`),
    ]);

    return {
      props: {
        company,
        portfolio,
        availabilities,
        reviewsWithParams,
        reviews,
      },
    };
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    return redirectToHome;
  }
};

export default InstitutionalPage;
